import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useEnvironmentNavigation } from '../../hooks/useEnvironmentNavigation';
import { useFreelancerProfileProjects } from '../../hooks/useFreelancerProfileProjects';
import { Project } from '../../models/Project';
import ProjectListTable from '../../components/project/ProjectListTable';

const FreelancerProjects: React.FC = () => {
  const { t } = useTranslation();
  const { navigateByEnvironment } = useEnvironmentNavigation();
  const [searchParams] = useSearchParams();

  // State for pagination and filters
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [projectNumberFilter, setProjectNumberFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  // Read status from URL on mount and when URL changes
  useEffect(() => {
    const statusFromUrl = searchParams.get('status');
    if (statusFromUrl) {
      setStatusFilter(statusFromUrl);
    } else {
      setStatusFilter('');
    }
  }, [searchParams]);
  
  const { data, isLoading, error } = useFreelancerProfileProjects(
    page,
    pageSize,
    statusFilter,
    nameFilter,
    projectNumberFilter,
    sortField,
    sortDirection
  );

  const handleViewProject = (id: number) => {
    navigateByEnvironment(`/projects/${id}`);
  };
  
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };
  
  const handleSortChange = (field: string, direction: string) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
  };
  
  const handleSearch = () => {
    setPage(1);
  };
  
  const handleReset = () => {
    setStatusFilter('');
    setNameFilter('');
    setProjectNumberFilter('');
    setPage(1);
  };

  // Freelancers can't edit or create projects
  const canEditProject = (_project: Project) => false;

  const projects = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  return (
    <ProjectListTable
      projects={projects}
      totalCount={totalCount}
      totalPages={totalPages}
      isLoading={isLoading}
      error={error}
      canCreateProject={false} // Freelancers can't create projects
      showFeePercentage={false} // Freelancers don't see fee percentage
      showCompanyFilter={false} // Freelancers don't need company filter
      title={t('menu.myProjects')} // Use the same translation key as the menu
      createButtonText=""
      onCreateProject={() => {}} // Not used
      onViewProject={handleViewProject}
      onEditProject={() => {}} // Not used
      canEditProject={canEditProject}
      page={page}
      pageSize={pageSize}
      statusFilter={statusFilter}
      nameFilter={nameFilter}
      projectNumberFilter={projectNumberFilter}
      sortField={sortField}
      sortDirection={sortDirection}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      onStatusFilterChange={setStatusFilter}
      onNameFilterChange={setNameFilter}
      onProjectNumberFilterChange={setProjectNumberFilter}
      onCompanyFilterChange={() => {}}
      onSortChange={handleSortChange}
      onSearch={handleSearch}
      onReset={handleReset}
    />
  );
};

export default FreelancerProjects;